import { useEffect, memo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { assign, actions, send, createMachine } from 'xstate';
import { atomWithMachine } from 'jotai-xstate';
import { Provider, atom, useAtom, createStore } from 'jotai';
import Honeybadger from '@honeybadger-io/js';
import { LoaderBalloonScreen } from '@crazyegginc/hatch';
import { fromUnixTime, differenceInMilliseconds, subMilliseconds } from 'date-fns';

import { AuthErrorPage } from '/src/features/_global/pages/AuthErrorPage';
import { NoAccountsErrorPage } from '/src/features/_global/pages/NoAccountsErrorPage';
import {
  decodedToken,
  goToLogin,
  goToCurrentDashboard,
  semrushToCeToken,
  updateClockSkewFromApi,
} from '/src/utils/auth';
import { clearCookies } from '/src/utils/cookies';
import { selectivelyClearLocalStorage } from '/src/utils/storage';
import { identifyUser, clearContext as clearHoneybadgerCtx, isBotUserAgent } from '/src/utils/honeybadger';
import { getShareTokenPayload, getShareCodeFromUrl, isTransportErrorLike, checkSharingSession } from '/src/utils';
import { AUTH_TOKEN_KEY, AUTH_TOKEN_EXPIRATION_OFFSET_MS } from '/src/features/_global/constants';
import {
  meQuery,
  sessionInfoQuery,
  sharedResourceQuery,
  capabilitiesQuery,
  subscriptionQuery,
} from '/src/features/_global/queries';
import { gettingStartedQuery } from '/src/features/getting-started/queries';
import { AppConfig } from '../AppConfig';

// states
const INITIALIZE_SHARING = 'INITIALIZE_SHARING';
const INITIALIZE_APP = 'INITIALIZE_APP';
const FETCH_TOKEN = 'FETCH_TOKEN';
const FETCH_SHARED_RESOURCE = 'FETCH_SHARED_RESOURCE';
const AUTHENTICATED = 'AUTHENTICATED';
const APP_AUTHENTICATED = 'APP_AUTHENTICATED';
const UNAUTHENTICATED = 'UNAUTHENTICATED';
const ACCOUNT_ERROR = 'ACCOUNT_ERROR';
export const FETCH_INITIAL_DATA = 'FETCH_INITIAL_DATA';
export const ERROR = 'ERROR';
export const PRESENT_SHARED_RESOURCE = 'PRESENT_SHARED_RESOURCE';
export const REDIRECT_TO_SHARED_RESOURCE = 'REDIRECT_TO_SHARED_RESOURCE';
export const SHARED_RESOURCE_NOT_FOUND = 'SHARED_RESOURCE_NOT_FOUND';
const HANDLE_SHARED_RESOURCE = 'HANDLE_SHARED_RESOURCE';
const LOGGED_OUT = 'LOGGED_OUT';

// internal actions
const INITIALIZED = 'INITIALIZED';
// const TOKEN = 'TOKEN';
const RECEIVED_SHARED_RESOURCE = 'RECEIVED_SHARED_RESOURCE';
const RETRY_FETCH_TOKEN = 'RETRY_FETCH_TOKEN';
const NO_ACCOUNTS = 'NO_ACCOUNTS';

// exposed actions
export const REFETCH_TOKEN = 'REFETCH_TOKEN';
export const LOGOUT = 'LOGOUT';
export const SWITCH_ACCOUNT = 'SWITCH_ACCOUNT';
export const RECEIVED_INITIAL_DATA = 'RECEIVED_INITIAL_DATA';
export const ERRORED_INITIAL_DATA = 'ERRORED_INITIAL_DATA';
export const REFETCH_USER_DATA = 'REFETCH_USER_DATA';

const initialAuthAtom = atom(INITIALIZE_APP); // alternatively can be `INITIALIZE_SHARING`

const AUTH_ERROR_MESSAGES = ['Expired token', 'Invalid token', 'Malformed token', 'No user session'];
const SEMRUSH_TOKEN_REFRESH_LEEWAY_MS = 2 * 60 * 1000;
const STARTUP_TRANSPORT_NOTIFY_AFTER_RETRIES = 1;
const STARTUP_QUERY_RETRY_COUNT = 1;
const STARTUP_FLOW_RETRY_COUNT = 1;

function tokenRefreshOffsetMs(isSemrushSession) {
  return isSemrushSession ? SEMRUSH_TOKEN_REFRESH_LEEWAY_MS : AUTH_TOKEN_EXPIRATION_OFFSET_MS;
}

function getErrorType(error) {
  return error?.graphQLErrors?.[0]?.originalError?.type || error?.graphQLErrors?.[0]?.extensions?.type;
}

function getErrorMessage(error) {
  return error?.graphQLErrors?.[0]?.originalError?.message || error?.message || '';
}

function isAuthError(error) {
  const message = getErrorMessage(error);
  const type = getErrorType(error);
  return AUTH_ERROR_MESSAGES.some((msg) => message.indexOf(msg) > -1) || type === 'authentication';
}

function serializeStartupError(error) {
  if (!error) return null;

  return {
    name: error?.name,
    message: getErrorMessage(error),
    graphQLErrors: (error?.graphQLErrors ?? []).map((graphQLError) => ({
      message: graphQLError?.message,
      type: graphQLError?.originalError?.type || graphQLError?.extensions?.type,
      path: graphQLError?.path,
    })),
    networkError: error?.networkError
      ? {
          message: error.networkError.message,
          statusCode: error.networkError.statusCode,
        }
      : null,
  };
}

function tokenMsUntilExpiration(accessToken, eventData = {}) {
  if (!accessToken) return 0;

  const payload = decodedToken(accessToken);
  if (!payload?.exp) return 0;

  const localTime = new Date();

  if (eventData?.is_semrush) {
    const skewMs = (eventData?.clockSkewSeconds ?? 0) * 1000;
    const adjustedLocalTime = subMilliseconds(localTime, skewMs);
    return differenceInMilliseconds(fromUnixTime(payload.exp), adjustedLocalTime);
  }

  const serverTime = payload?.iat ? fromUnixTime(payload.iat) : localTime;
  const clockSkewMs = differenceInMilliseconds(localTime, serverTime);
  const adjustedLocalTime = subMilliseconds(localTime, clockSkewMs);

  return differenceInMilliseconds(fromUnixTime(payload.exp), adjustedLocalTime);
}

async function invokeFetchAuthToken() {
  // if the user is a SEMRush user return early with the supplied token
  if (window.SM) {
    try {
      let rawToken = await window.SM.client('getAccessToken');
      let token = semrushToCeToken(rawToken);
      let clockSkewSeconds = 0;

      try {
        const skew = await updateClockSkewFromApi();
        clockSkewSeconds = Number.isFinite(skew) ? skew : 0;
      } catch (err) {
        Honeybadger.notify('Failed to get clock skew from API for SEMRush token', {
          context: {
            error: err.message,
          },
        });
      }

      const msUntilSemrushTokenExpiration = tokenMsUntilExpiration(token.access_token, {
        ...token,
        clockSkewSeconds,
      });

      if (msUntilSemrushTokenExpiration <= tokenRefreshOffsetMs(true)) {
        rawToken = await window.SM.client('getAccessToken');
        const refreshedToken = semrushToCeToken(rawToken);
        const refreshedTokenExpirationMs = tokenMsUntilExpiration(refreshedToken.access_token, {
          ...refreshedToken,
          clockSkewSeconds,
        });

        if (refreshedTokenExpirationMs > msUntilSemrushTokenExpiration) {
          token = refreshedToken;
        }
      }

      window.dispatchEvent(new CustomEvent('auth:received_token', { detail: { token } }));

      return { ...token, clockSkewSeconds };
    } catch (err) {
      const classification = isTransportErrorLike(err) ? 'transport' : 'application';
      Honeybadger.notify(`Semrush auth fetch ${classification} failed`, {
        fingerprint: `semrush-auth-fetch-${classification}`,
        context: {
          err: getErrorMessage(err),
          online: navigator.onLine,
          auth_base_url: AppConfig.authBaseURL(),
        },
      });
    }
  }

  try {
    const response = await fetch(`${AppConfig.authBaseURL()}/authenticate`, {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      redirect: 'follow',
    });

    if (response.redirected) {
      return window.location.replace(response.url);
    }

    if (!response.ok) {
      let errorResponse = {};

      try {
        errorResponse = await response.json();
      } catch {
        errorResponse = {};
      }

      if (response.status === 401 || errorResponse?.error === 'unauthorized') {
        return { error: 'unauthorized' };
      }

      return Object.keys(errorResponse).length > 0 ? errorResponse : { error: 'failed_auth_fetch' };
    }

    const token = await response.json();

    if (token.access_token) {
      window.dispatchEvent(new CustomEvent('auth:received_token', { detail: { token } }));
    }

    return token;
  } catch (error) {
    const classification = isTransportErrorLike(error) ? 'transport' : 'application';
    Honeybadger.notify(`User auth fetch ${classification} failed`, {
      fingerprint: `user-auth-fetch-${classification}`,
      context: {
        error: error.message,
        stack: error.stack,
        online: navigator.onLine,
        auth_base_url: AppConfig.authBaseURL(),
      },
    });
    throw new Error('User auth fetch failed');
  }
}

async function performAccountSwitch(context, event) {
  const rawNewToken = await fetch(`${AppConfig.authBaseURL()}/switch?account_holder_id=${event.accountId}`, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  });
  return await rawNewToken.json();
}

export async function signOut() {
  return window.location.replace(`${AppConfig.authBaseURL()}/logout`);
}

function handleAccountMessage({ message }) {
  if (message === 'logout') {
    goToLogin();
  } else if (message === 'accountSwitch') {
    goToCurrentDashboard();
  } else if (message === 'unauthenticated') {
    goToLogin(true);
  }
}

const initialAuthContext = {
  token: null,
  connected: false,
  isSharing: false,
  sessionInfo: null,
  currentUser: null,
  currentAccount: null,
  sharedResource: null,
  refetchAttempts: 0,
  sharingCode: null,
  msUntilTokenExpiration: null,
  permittedAccounts: [],
  isSemrush: false,
};

const createAuthMachine = (initial) => {
  return createMachine(
    {
      id: 'auth',
      // https://xstate.js.org/docs/guides/actions.html
      predictableActionArguments: true,
      preserveActionOrder: true,
      initial,
      context: initialAuthContext,
      states: {
        // initial states
        [INITIALIZE_APP]: {
          on: {
            [INITIALIZED]: {
              actions: [
                assign({
                  isSharing: false,
                }),
              ],
              target: FETCH_TOKEN,
            },
          },
        },
        [INITIALIZE_SHARING]: {
          on: {
            [INITIALIZED]: {
              actions: [
                assign({
                  isSharing: true,
                }),
              ],
              target: FETCH_SHARED_RESOURCE,
            },
          },
        },
        // loading states
        [FETCH_TOKEN]: {
          invoke: {
            id: 'fetch-auth-token',
            src: invokeFetchAuthToken,
            onDone: [
              {
                target: AUTHENTICATED,
                cond: 'isValidJwt',
                actions: [
                  assign({
                    refetchAttempts: 0,
                    token: (context, event) => event.data?.access_token,
                    tokenPayload: (context, event) =>
                      event.data?.access_token ? decodedToken(event.data.access_token) : null,
                    permittedAccounts: (context, event) => event.data?.permitted_accounts ?? [],
                    isSemrush: (context, event) => event.data?.is_semrush ?? false,
                  }),
                  assign({
                    msUntilTokenExpiration: (_context, event) => {
                      return tokenMsUntilExpiration(event.data?.access_token, event.data);
                    },
                  }),
                  (context, event) => {
                    window.localStorage.setItem(AUTH_TOKEN_KEY, event.data?.access_token);
                  },
                ],
              },
              {
                target: UNAUTHENTICATED,
                cond: 'isUnauthorizedAuthResponse',
              },
              {
                target: UNAUTHENTICATED,
                cond: 'hasNoAccessToken',
              },
              {
                target: RETRY_FETCH_TOKEN,
                cond: 'isExpiredJwtAndCanRetry',
              },
              {
                target: UNAUTHENTICATED,
                cond: 'isExpiredJwt',
              },
              {
                target: ACCOUNT_ERROR,
                cond: (ctx, e) => {
                  if (e?.data?.access_token && e?.data?.permitted_accounts && e.data.permitted_accounts.length === 0) {
                    return true;
                  }
                  return false;
                },
              },
              { target: UNAUTHENTICATED },
            ],
            onError: [
              {
                target: UNAUTHENTICATED,
                cond: (ctx, e) => {
                  return (e?.data?.error ?? null) === 'unauthorized';
                },
              },
              {
                target: RETRY_FETCH_TOKEN,
                cond: 'canRetryTokenFetch',
              },
              {
                target: ERROR,
              },
            ],
          },
        },
        [RETRY_FETCH_TOKEN]: {
          after: {
            REFETCH_DELAY: {
              actions: [
                assign({
                  refetchAttempts: (context) => context.refetchAttempts + 1,
                }),
              ],
              target: FETCH_TOKEN,
            },
          },
        },
        RETRY_FETCH_INITIAL_DATA: {
          after: {
            REFETCH_DELAY: {
              actions: [
                assign({
                  refetchAttempts: (context) => context.refetchAttempts + 1,
                }),
              ],
              target: FETCH_INITIAL_DATA,
            },
          },
        },
        [FETCH_SHARED_RESOURCE]: {
          entry: [
            assign({
              sharingCode: getShareCodeFromUrl() || getShareTokenPayload()?.shared_item?.code,
            }),
            actions.pure((context) => {
              if (!context.sharingCode || context.sharingCode === '') {
                return send('SHARED_RESOURCE_NOT_FOUND');
              }
            }),
          ],
          on: {
            [RECEIVED_SHARED_RESOURCE]: {
              target: HANDLE_SHARED_RESOURCE,
              actions: [
                assign({
                  sharedResource: (context, event) => event.data?.sharedResource,
                }),
              ],
            },
            [SHARED_RESOURCE_NOT_FOUND]: {
              target: SHARED_RESOURCE_NOT_FOUND,
            },
          },
        },
        [HANDLE_SHARED_RESOURCE]: {
          always: [
            {
              target: PRESENT_SHARED_RESOURCE,
              cond: 'hasPresentableSharedResource',
            },
            {
              target: REDIRECT_TO_SHARED_RESOURCE,
              cond: 'hasRedirectableSharedResource',
            },
            { target: SHARED_RESOURCE_NOT_FOUND },
          ],
        },
        [PRESENT_SHARED_RESOURCE]: {
          on: {},
          entry: [
            assign({
              token: (ctx) => ctx.sharedResource.resource.authToken,
            }),
            (ctx) => {
              window.shr = ctx.sharedResource.resource.authToken;
            },
          ],
        },
        [REDIRECT_TO_SHARED_RESOURCE]: {
          type: 'final',
          entry: [
            (context) => {
              const searchParams = new URLSearchParams(window.location.search);
              if (Array.from(searchParams.keys()).length > 0) {
                window.location.href = `${context.sharedResource.resource.url}?${searchParams.toString()}`;
              } else {
                window.location.href = context.sharedResource.resource.url;
              }
            },
          ],
        },
        [SHARED_RESOURCE_NOT_FOUND]: {
          type: 'final',
        },
        [FETCH_INITIAL_DATA]: {
          on: {
            [RECEIVED_INITIAL_DATA]: [
              {
                actions: [
                  assign({
                    refetchAttempts: 0,
                    currentUser: (context, event) => ({
                      ...event.data.me,
                      accounts:
                        event.data.sessionInfo.isImpersonated || context.isSemrush
                          ? event.data.me.accounts
                          : event.data.me.accounts.filter((acc) => context.permittedAccounts.includes(acc.id)),
                    }),
                    sessionInfo: (context, event) => event.data.sessionInfo,
                    currentAccount: (context, event) => event.data.me.accounts.find((account) => account.isCurrent),
                    capabilities: (context, event) => event.data.capabilities,
                    subscription: (context, event) => event.data.subscription,
                    gettingStarted: (context, event) => event.data.gettingStarted,
                  }),
                ],
                target: AUTHENTICATED,
                cond: 'hasValidInitialData',
              },
              {
                // the payload wasn't valid, assume error
                target: 'RETRY_FETCH_INITIAL_DATA',
                cond: 'canRetryInitialDataFetch',
              },
              {
                target: 'ERROR',
              },
            ],
            [ERRORED_INITIAL_DATA]: [
              {
                target: FETCH_TOKEN,
                cond: 'isInitialDataAuthError',
              },
              {
                target: 'RETRY_FETCH_INITIAL_DATA',
                cond: 'canRetryInitialDataFetch',
              },
              {
                target: 'ERROR',
              },
            ],
            [NO_ACCOUNTS]: ACCOUNT_ERROR,
          },
        },
        // final states
        [AUTHENTICATED]: {
          always: [
            {
              target: FETCH_INITIAL_DATA,
              cond: 'sessionNotLoaded',
            },
            {
              target: APP_AUTHENTICATED,
            },
          ],
        },
        [APP_AUTHENTICATED]: {
          entry: [
            (context) => {
              identifyUser({
                userId: context.currentUser.userId,
                guestId: context.currentUser.guestId,
                email: context.currentUser.email,
                currentAccount: context.currentAccount,
              });

              if (window.CE2?.identify) {
                try {
                  window.CE2.identify(context.currentUser.email);
                } catch {
                  Honeybadger.notify('User script failed to identify', { context: { script: { ...window.CE2 } } });
                }
              } else {
                window.CE_READY = function () {
                  try {
                    window.CE2.identify(context.currentUser.email);
                  } catch {
                    Honeybadger.notify('User script missing identify function', {
                      context: { script: { ...window.CE2 } },
                    });
                  }
                };
              }
            },
          ],
          invoke: {
            src: () => (sendBack) => {
              function refetchAuth() {
                sendBack({ type: REFETCH_TOKEN });
              }
              window.addEventListener('auth:refetch_token', refetchAuth);
              return () => window.removeEventListener('auth:refetch_token', refetchAuth);
            },
          },
          on: {
            ONLINE: {
              target: FETCH_TOKEN,
            },
            [REFETCH_TOKEN]: {
              target: FETCH_TOKEN,
            },
            [REFETCH_USER_DATA]: {
              target: FETCH_INITIAL_DATA,
            },
            [LOGOUT]: {
              target: LOGGED_OUT,
            },
            [SWITCH_ACCOUNT]: {
              target: SWITCH_ACCOUNT,
            },
            [RECEIVED_INITIAL_DATA]: {
              actions: [
                assign({
                  currentUser: (context, event) => ({
                    ...event.data.me,
                    accounts:
                      event.data.sessionInfo.isImpersonated || context.isSemrush
                        ? event.data.me.accounts
                        : event.data.me.accounts.filter((acc) => context.permittedAccounts.includes(acc.id)),
                  }),
                  sessionInfo: (context, event) => event.data.sessionInfo,
                  currentAccount: (context, event) => event.data.me.accounts.find((account) => account.isCurrent),
                  capabilities: (context, event) => event.data.capabilities,
                  subscription: (context, event) => event.data.subscription,
                  gettingStarted: (context, event) => event.data.gettingStarted,
                }),
              ],
              cond: 'hasValidInitialData',
            },
          },
          after: {
            TOKEN_EXPIRATION: {
              target: FETCH_TOKEN,
            },
          },
        },
        [SWITCH_ACCOUNT]: {
          invoke: {
            id: 'account-switch',
            src: performAccountSwitch,
            onDone: {
              // target: FETCH_INITIAL_DATA,
              actions: [
                assign({
                  token: (context, event) => event?.data?.access_token || event?.data?.token,
                  permittedAccounts: (context, event) => event?.data?.permitted_accounts ?? [],
                }),
                (ctx) => {
                  window.localStorage.setItem(AUTH_TOKEN_KEY, ctx.token);
                  // ensure Urql cache is cleared
                  window.dispatchEvent(new Event('account:refetch'));
                  window.accountChannel?.postMessage?.({
                    message: 'accountSwitch',
                  });
                  // wait 200ms and then do full page reloads.
                  // TODO: replace this with Apollo
                  goToCurrentDashboard();
                },
              ],
            },
          },
        },
        [UNAUTHENTICATED]: {
          type: 'final',
          entry: [
            assign(initialAuthContext),
            () => {
              clearHoneybadgerCtx();
              clearCookies();
              selectivelyClearLocalStorage();
              window.sessionStorage.clear();
              window.accountChannel?.postMessage?.({
                message: 'unauthenticated',
              });
              goToLogin(true);
            },
          ],
        },
        [LOGGED_OUT]: {
          type: 'final',
          entry: [
            assign(initialAuthContext),
            () => {
              clearHoneybadgerCtx();
              clearCookies();
              selectivelyClearLocalStorage();
              window.sessionStorage.clear();
              window.accountChannel?.postMessage?.({
                message: 'logout',
              });
              signOut();
            },
          ],
        },
        [ACCOUNT_ERROR]: {
          type: 'final',
        },
        [ERROR]: {
          type: 'final',
          entry: [
            assign({
              refetchAttempts: 0,
            }),
            // we could do some Honeybadger reporting here
          ],
        },
      },
    },
    {
      delays: {
        TOKEN_EXPIRATION: (context) => {
          const refreshOffsetMs = tokenRefreshOffsetMs(context.isSemrush);

          // fetch before token expiration using the shared auth offset
          if (context.msUntilTokenExpiration <= refreshOffsetMs) {
            // immediately trigger expiration
            return 0;
          }
          return context.msUntilTokenExpiration - refreshOffsetMs;
        },
        REFETCH_DELAY: (ctx) => {
          return ctx.refetchAttempts * 1000;
        },
      },
      guards: {
        hasPermittedAccounts: (context, event) => {
          if (event?.data?.permitted_accounts && event.data.permitted_accounts.length > 0) {
            return true;
          }
          return false;
        },
        isValidJwt: (context, event) => {
          try {
            if (event?.data?.access_token) {
              const tokenPayload = decodedToken(event.data.access_token);
              if (!tokenPayload) {
                return false;
              }

              if (tokenMsUntilExpiration(event.data.access_token, event.data) <= 0) {
                return false;
              }

              if (event.data.is_semrush) {
                // skip permitted accounts checks for SEMRush users
                return true;
              }

              if (event.data.permitted_accounts.length === 0) {
                return false;
              }

              if (event.data.permitted_accounts.includes(tokenPayload.sub)) {
                return true;
              }

              Honeybadger.notify('User attempted to use invalid JWT token', {
                context: {
                  token: event.data.access_token,
                  permitted_accounts: event.data.permitted_accounts,
                },
              });
            }
            return false;
          } catch (error) {
            Honeybadger.notify('Failed to check JWT token', {
              context: {
                payload: {
                  ...context,
                },
                event: {
                  ...event,
                },
                error: error.message,
                stack: error.stack,
              },
            });
            return false;
          }
        },
        isExpiredJwt: (_context, event) => {
          if (!event?.data?.access_token) {
            return false;
          }

          return tokenMsUntilExpiration(event.data.access_token, event.data) <= 0;
        },
        isExpiredJwtAndCanRetry: (context, event) => {
          if (!event?.data?.access_token) {
            return false;
          }

          return context.refetchAttempts < 3 && tokenMsUntilExpiration(event.data.access_token, event.data) <= 0;
        },
        isUnauthorizedAuthResponse: (_context, event) => {
          return (event?.data?.error ?? null) === 'unauthorized';
        },
        hasNoAccessToken: (_context, event) => {
          return !event?.data?.access_token;
        },
        canRetryTokenFetch: (context) => context.refetchAttempts < 3,
        canRetryInitialDataFetch: (context) => context.refetchAttempts < STARTUP_FLOW_RETRY_COUNT,
        isInitialDataAuthError: (_context, event) => {
          return event?.isAuthError === true || isAuthError(event?.error);
        },
        sessionNotLoaded: (context) => !context.currentUser,
        hasPresentableSharedResource: (context) => {
          return context.sharedResource.type === 'RESOURCE';
        },
        hasRedirectableSharedResource: (context) => {
          return context.sharedResource.type === 'REDIRECT';
        },
        hasValidInitialData: (context, e) => {
          return (
            e?.data?.sessionInfo &&
            e?.data?.me &&
            e?.data?.capabilities &&
            e?.data.subscription &&
            (context.isSharing || e?.data?.gettingStarted) &&
            e?.data?.me?.accounts &&
            e.data.me.accounts.length > 0 &&
            e.data.me.accounts.some((account) => account.isCurrent)
          );
        },
      },
    },
  );
};

// this is the only way you should interact with the authMachine in any way from
// anywhere in the app.
export const authAtom = atomWithMachine((get) => createAuthMachine(get(initialAuthAtom)), {
  devTools: import.meta.env.DEV,
});

// The Jotai provider in this component is an isolated tree,
// it also provides the initial values to the contained machine.
// It is wrapped in a Suspense component because there are
// async behaviours contained inside.
// NOTE: please be sure that there is only ever one AuthProvider in the tree at any given moment.
export const AuthProvider = memo(({ children }) => {
  const isSharedSession = checkSharingSession();

  if (!isSharedSession) {
    window.ce_sharingSession = false;
  }

  const store = createStore();
  store.set(initialAuthAtom, isSharedSession ? INITIALIZE_SHARING : INITIALIZE_APP);

  return (
    <Provider store={store}>
      <AuthFlow>{children}</AuthFlow>
    </Provider>
  );
});

function AdminAuthFlow() {
  useEffect(() => {
    // remove anything that might hinder impesonation sessions
    window.sessionStorage.clear();
    window.localStorage.clear();

    // get redirectTo from query params and include any other params in the destinatin url
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirectTo');
    const destination = new URL(redirectTo || '/', window.location.origin);
    params.forEach((value, key) => {
      if (key === 'redirectTo') return;
      destination.searchParams.set(key, value);
    });
    window.location.replace(destination.toString());
  }, []);

  return <LoaderBalloonScreen text={`Preparing impersonation...`} />;
}

function UserAuthFlow({ children }) {
  // ensure the machine starts up
  const [state, send] = useAtom(authAtom);
  const reportedStartupErrorsRef = useRef(new Set());
  const { pathname } = window.location;

  useEffect(() => {
    if (window.location.search.indexOf('debug=auth') >= 0) {
      Honeybadger.notify('Auth debug', {
        context: {
          value: state.value,
          state: state.context,
        },
      });
    }
  }, [state]);

  const enabled = [FETCH_INITIAL_DATA, APP_AUTHENTICATED].includes(state.value);

  const meResult = useQuery({
    ...meQuery(),
    enabled,
    retry: STARTUP_QUERY_RETRY_COUNT,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });
  const capabilitiesResult = useQuery({
    ...capabilitiesQuery(),
    enabled,
    retry: STARTUP_QUERY_RETRY_COUNT,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });
  const subscriptionResult = useQuery({
    ...subscriptionQuery(),
    enabled,
    retry: STARTUP_QUERY_RETRY_COUNT,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });
  const sessionInfoResult = useQuery({
    ...sessionInfoQuery(),
    enabled,
    retry: STARTUP_QUERY_RETRY_COUNT,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });
  const gettingStartedResult = useQuery({
    ...gettingStartedQuery(),
    enabled: enabled && !state.context.isSharing,
    retry: STARTUP_QUERY_RETRY_COUNT,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });

  const sharedResourceResult = useQuery({
    ...sharedResourceQuery({ sharingCode: state.context.sharingCode }),
    enabled: Boolean(state.value === FETCH_SHARED_RESOURCE && state.context.sharingCode),
  });

  useEffect(() => {
    if (state.value === FETCH_INITIAL_DATA && meResult?.data?.me?.accounts?.length === 0) {
      send(NO_ACCOUNTS);
    }
  }, [meResult?.data?.me?.accounts?.length, state.value, send]);

  useEffect(() => {
    if (state.value === INITIALIZE_APP || state.value === INITIALIZE_SHARING) {
      send({
        type: INITIALIZED,
      });
    }
  }, [send, state.value]);

  useEffect(() => {
    if (
      state.value === FETCH_INITIAL_DATA &&
      (meResult.error ||
        sessionInfoResult.error ||
        capabilitiesResult.error ||
        subscriptionResult.error ||
        gettingStartedResult.error)
    ) {
      const startupQueryErrors = {
        me: meResult.error ?? null,
        sessionInfo: sessionInfoResult.error ?? null,
        capabilities: capabilitiesResult.error ?? null,
        subscription: subscriptionResult.error ?? null,
        gettingStarted: gettingStartedResult.error ?? null,
      };

      const failedStartupEntries = Object.entries(startupQueryErrors).filter(([, errorValue]) => Boolean(errorValue));
      const errors = failedStartupEntries.map(([, errorValue]) => errorValue);
      const failedOperations = failedStartupEntries.map(([operation]) => operation);
      const firstError = errors[0];
      const hasAuthError = errors.some(isAuthError);
      const isBotUser = isBotUserAgent();

      const message = getErrorMessage(firstError);
      const wholeStartupFailed = failedStartupEntries.length === Object.keys(startupQueryErrors).length;
      const allTransportErrors = failedStartupEntries.every(([, errorValue]) => isTransportErrorLike(errorValue));
      const uniqueTransportMessages = new Set(
        failedStartupEntries.map(([, errorValue]) => getErrorMessage(errorValue)).filter(Boolean),
      );
      const allTransportErrorsShareMessage = allTransportErrors && uniqueTransportMessages.size === 1;
      const shouldSuppressTransportOnlyStartupError =
        allTransportErrors && state.context.refetchAttempts < STARTUP_TRANSPORT_NOTIFY_AFTER_RETRIES;
      const errorClassification = allTransportErrors ? 'transport' : 'application';

      if (
        message &&
        !isBotUser &&
        !hasAuthError &&
        !['User not found'].some((msg) => message.indexOf(msg) > -1) &&
        !shouldSuppressTransportOnlyStartupError
      ) {
        const fingerprint = [
          'initial-data-fetch',
          errorClassification,
          ...failedOperations.sort((a, b) => a.localeCompare(b)),
        ].join('-');

        if (!reportedStartupErrorsRef.current.has(fingerprint)) {
          reportedStartupErrorsRef.current.add(fingerprint);
          Honeybadger.notify(`Initial data fetch ${errorClassification} error`, {
            fingerprint,
            context: {
              local_time: new Date().getTime(),
              has_token: Boolean(window.localStorage.getItem(AUTH_TOKEN_KEY)),
              message,
              pathname,
              online: navigator.onLine,
              auth_state: state.value,
              refetch_attempts: state.context.refetchAttempts,
              gql_origin: window.CORE_API_URL ?? null,
              gql_url: window.CORE_API_URL ? `${window.CORE_API_URL}/gql` : null,
              failed_operations: failedOperations,
              wholeStartupFailed,
              allTransportErrors,
              allTransportErrorsShareMessage,
              startup_errors: Object.fromEntries(
                Object.entries(startupQueryErrors).map(([operation, operationError]) => [
                  operation,
                  serializeStartupError(operationError),
                ]),
              ),
            },
          });
        }
      } else if (!isBotUser && shouldSuppressTransportOnlyStartupError) {
        Honeybadger.addBreadcrumb('Initial data fetch transport failure suppressed', {
          category: 'error',
          metadata: {
            pathname,
            online: navigator.onLine,
            failedOperations,
            message,
            retry_attempts: state.context.refetchAttempts,
          },
        });
      }

      send({
        type: ERRORED_INITIAL_DATA,
        error: firstError,
        isAuthError: hasAuthError,
      });
    }
  }, [
    meResult.error,
    sessionInfoResult.error,
    capabilitiesResult.error,
    subscriptionResult.error,
    gettingStartedResult.error,
    pathname,
    state.value,
    state.context.refetchAttempts,
    send,
  ]);

  useEffect(() => {
    if (
      [FETCH_INITIAL_DATA, APP_AUTHENTICATED].includes(state.value) &&
      meResult.data &&
      sessionInfoResult.data &&
      capabilitiesResult.data &&
      subscriptionResult.data &&
      gettingStartedResult.data
    ) {
      send({
        type: RECEIVED_INITIAL_DATA,
        data: {
          ...meResult.data,
          ...sessionInfoResult.data,
          ...capabilitiesResult.data,
          ...subscriptionResult.data,
          ...gettingStartedResult.data,
        },
      });
    }
  }, [
    meResult.data,
    sessionInfoResult.data,
    capabilitiesResult.data,
    subscriptionResult.data,
    gettingStartedResult.data,
    state.value,
    send,
  ]);

  useEffect(() => {
    if (state.value === FETCH_SHARED_RESOURCE && sharedResourceResult?.data) {
      send({
        type: RECEIVED_SHARED_RESOURCE,
        data: sharedResourceResult.data,
      });
    } else if (state.value === FETCH_SHARED_RESOURCE && sharedResourceResult?.error) {
      send({
        type: SHARED_RESOURCE_NOT_FOUND,
      });
    }
  }, [send, sharedResourceResult?.data, sharedResourceResult?.error, state.value]);

  useEffect(() => {
    window.accountChannel?.addEventListener?.('message', handleAccountMessage);

    return () => window.accountChannel?.removeEventListener?.('message', handleAccountMessage);
  }, []);

  useEffect(() => {
    function sendOnline() {
      send('ONLINE');
    }

    function sendOffline() {
      send('OFFLINE');
    }

    function handleTokenError() {
      send(REFETCH_TOKEN);
    }

    window.addEventListener('offline', sendOffline);
    window.addEventListener('online', sendOnline);
    window.addEventListener('gql:tokenError', handleTokenError);

    return () => {
      window.removeEventListener('offline', sendOffline);
      window.removeEventListener('online', sendOnline);
      window.removeEventListener('gql:tokenError', handleTokenError);
    };
  }, [send]);

  // we can declare global type of listeners here
  // but please keep them to a minimum 😅

  if (
    [
      SWITCH_ACCOUNT,
      FETCH_INITIAL_DATA,
      FETCH_SHARED_RESOURCE,
      'RETRY_FETCH_INITIAL_DATA',
      'RETRY_FETCH_TOKEN',
    ].includes(state.value)
  ) {
    return (
      <>
        {state.context.refetchAttempts > 0 && state.context.refetchAttempts <= 3 ? (
          <LoaderBalloonScreen text={`Retry ${state.context.refetchAttempts} of 3...`} />
        ) : state.context.refetchAttempts > 0 ? (
          <LoaderBalloonScreen text={false} />
        ) : (
          <LoaderBalloonScreen text={true} />
        )}
      </>
    );
  }

  if (state.value === ACCOUNT_ERROR) {
    return <NoAccountsErrorPage />;
  }

  if (state.value === ERROR) {
    return <AuthErrorPage />;
  }

  if (!state.context?.currentUser && !state.context.isSharing) {
    return <LoaderBalloonScreen text={true} />;
  }

  return children;
}

function AuthFlow({ children }) {
  const { pathname } = window.location;

  if (pathname === '/admin/impersonate') {
    return <AdminAuthFlow />;
  }

  return <UserAuthFlow>{children}</UserAuthFlow>;
}

export function refetchAuthToken() {
  return new Promise((resolve) => {
    // dispatch the global event and wait for the new token
    window.addEventListener(
      'auth:received_token',
      (e) => {
        resolve(e.detail.token);
      },
      { once: true },
    );

    window.dispatchEvent(new CustomEvent('auth:refetch_token'));
  });
}
